import { FC, useEffect, useState, useMemo, ReactNode } from "react";
import { Outlet, useNavigate, useRouter } from "@tanstack/react-router";
import styles from "./ShopLayout.module.css";
import { FireIcon } from "@shared/ui/icon/Fire";
import { SidebarShop } from "../../shared/ui/SideBar/Sidebar";
import { useSelector } from "react-redux";
import {
  selectUserBalanceGCoin,
  selectUserBalancePyl,
} from "@entities/User/model/slice";
import { BuyMoreButton } from "@shared/ui/Buttons/BuyMoreButton/BuyMoreButton";


export const SHOP_PAGE_TEXTS: Record<string, ReactNode> = {
  "/shop": <img src="/text/Day.svg" style={{ transform: 'scale(0.8)'}}/>,
  "/shop/select-game": <img src="/text/Select.svg"/>, // Add your image path here
  "/shop/select-case": <img src="/text/Select.svg"/>,
  "/shop/select-game/top-skins": <img src="/text/Game.svg" style={{ transform: 'scale(0.8)' }}/>,
  "/shop/select-game/archive": <img src="/text/Game.svg" style={{ transform: 'scale(0.8)' }}/>,
  "/shop/cash": <img src="/text/vip.svg" style={{ transform: 'scale(0.8)' }}/>, // Add your image path here
  "/shop/vip":  <img src="/text/Select.svg"/>,
};

// Типы путей для безопасной типизации
export type ShopPath = keyof typeof SHOP_PAGE_TEXTS;

export interface NavItem {
  path: ShopPath;
  label: string;
}

const navItems: NavItem[] = [
  { path: "/shop", label: "MAIN" },
  { path: "/shop/select-game", label: "SKINS" },
  { path: "/shop/select-case", label: "CASES" },
  { path: "/shop/cash", label: "CASH" },
  { path: "/shop/vip", label: "VIP" },
];

export const ShopLayout: FC = () => {
  const userBalancePyl = useSelector(selectUserBalancePyl);
  const userBalanceGcoin = useSelector(selectUserBalanceGCoin);
  const [currentText, setCurrentText] = useState<React.ReactNode>("GOON STORE");

  const navigate = useNavigate();
  const router = useRouter();

  // Мемоизированный список путей, отсортированный от более конкретных к менее конкретным
  const sortedPaths = useMemo(() => {
    return Object.keys(SHOP_PAGE_TEXTS).sort((a, b) => {
      // Сначала сравниваем по длине (длинные пути более конкретные)
      const lengthDiff = b.length - a.length;
      if (lengthDiff !== 0) return lengthDiff;
      
      // При одинаковой длине, сортируем по алфавиту для стабильной сортировки
      return a.localeCompare(b);
    });
  }, []);

  // Редирект, если нет баланса
  useEffect(() => {
    if (!userBalancePyl) {
      navigate({ to: "/lobby" });
    }
  }, [navigate, userBalancePyl]);

  // Обновляем текст заголовка при изменении пути
  useEffect(() => {
    const unlisten = router.subscribe('onBeforeNavigate', () => {
      const currentPath = router.state.location.pathname;
  
      const getCurrentText = () => {
        if (currentPath in SHOP_PAGE_TEXTS) {
          return SHOP_PAGE_TEXTS[currentPath as ShopPath];
        }
  
        for (const path of sortedPaths) {
          if (currentPath.startsWith(path)) {
            return SHOP_PAGE_TEXTS[path as ShopPath];
          }
        }
  
        return SHOP_PAGE_TEXTS["/shop"];
      };
  
      setCurrentText(getCurrentText());
    });
  
    return () => {
      unlisten();
    };
  }, [router, sortedPaths]);

  return (
    <div className={styles.container}>
      <div className={styles.shopLayout}>
        <div className={styles.sidebar}>
          <SidebarShop navItems={navItems} />
        </div>
        <div className={styles.mainTabletFrame}>
          <div className={styles.MainHeader}>
            <div className={styles.MainHeaderTitle}>GOON WENDING MACHINE</div>
            <div className={styles.centerHeaderContent}>
              <div className={styles.centerHC_content}>
                  {currentText} 
              </div>
            </div>
            <div className={styles.MainHeader__balance}>
              <div className={styles.money}>
                <FireIcon active="active" width={22} height={20} />{" "}
                {userBalancePyl}
              </div>
              <div className={styles.money}>
                <svg
                  width="22"
                  height="20"
                  viewBox="0 0 22 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g filter="url(#filter0_dd_424_7992)">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M11 20C16.5228 20 21 15.5228 21 10C21 4.47715 16.5228 0 11 0C5.47715 0 1 4.47715 1 10C1 15.5228 5.47715 20 11 20ZM12.25 3.75263V2.5H9.75V3.75313C8.78423 3.78474 8.03088 4.05586 7.48994 4.56648C6.91331 5.11081 6.625 5.89489 6.625 6.91874V13.0813C6.625 14.1051 6.91331 14.8892 7.48994 15.4335C8.03088 15.9441 8.78423 16.2153 9.75 16.2469V17.501H12.25V16.2474C13.2124 16.2186 13.9658 15.9473 14.5101 15.4335C15.0867 14.8762 15.375 14.0921 15.375 13.0813V9.83476C15.375 9.53668 15.2208 9.38764 14.9124 9.38764H11.3922C11.1106 9.38764 10.9698 9.52372 10.9698 9.79588V11.1178C10.9698 11.39 11.1106 11.526 11.3922 11.526H12.5991C12.7466 11.526 12.8204 11.5973 12.8204 11.7399V12.9646C12.8204 13.3793 12.7332 13.6774 12.5589 13.8589C12.3846 14.0273 12.0829 14.1116 11.6537 14.1116H10.3463C9.93056 14.1116 9.62883 14.0273 9.44109 13.8589C9.26676 13.6774 9.1796 13.3793 9.1796 12.9646V7.03538C9.1796 6.62066 9.26676 6.32906 9.44109 6.16057C9.62883 5.97913 9.93056 5.88841 10.3463 5.88841H11.6537C12.0829 5.88841 12.3846 5.97913 12.5589 6.16057C12.7332 6.32906 12.8204 6.62066 12.8204 7.03538V7.50194C12.8204 7.78707 12.9612 7.92963 13.2428 7.92963H14.9526C15.2342 7.92963 15.375 7.78707 15.375 7.50194V6.91874C15.375 5.90785 15.0867 5.13025 14.5101 4.58593C13.9658 4.05991 13.2124 3.78215 12.25 3.75263Z"
                      fill="#F2EB56"
                    />
                  </g>
                </svg>
                {userBalanceGcoin || 0}
              </div>
              <BuyMoreButton className={styles.BuyMore} />
            </div>
          </div>
          {/* Основной контент */}
          <div className={styles.shopLayout__main}>
            <div id="shop-frame" className={styles.shopFrame}></div>
            <div className={styles.mainContent}>
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
